import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatAgo, plural, useAdmin, useDebounced } from '../context';
import { Icon } from '../icons';
import { StaffAudit, StaffCandidate, StaffMemberView, StaffRole, StaffState } from '../types';
import { Empty, IconButton, Loading, Segmented } from '../ui';
import { DISCORD_ID_RE, MemberStatus, describeExpiry, extendedExpiry, finalCaps, grantable, roleOf, statusOf } from './helpers';
import { Draft, MemberEditor } from './MemberEditor';
import { RoleChip, StatusPill } from './parts';

type StatusFilter = 'all' | 'active' | 'suspended' | 'expired' | 'online';
type SortKey = 'name' | 'seen' | 'role';
type Layout = 'cards' | 'table';
type EditorState = null | { kind: 'add' } | { kind: 'member'; discordId: string; seed: { name: string; discordId: string } | null };

interface Props {
  staff: StaffState;
  members: StaffMemberView[];
  audit: StaffAudit | null;
  patchLocal: (discordId: string, patch: Partial<StaffMemberView>) => void;
}

const lastSeen = (m: StaffMemberView, now: number): string => (m.online ? 'En ligne' : m.lastSeenAt ? formatAgo(m.lastSeenAt, now) : 'Jamais connecté');

export const MembersTab: React.FC<Props> = ({ staff, members, audit, patchLocal }) => {
  const { act, confirm } = useAdmin();
  const [search, setSearch] = useState('');
  const dSearch = useDebounced(search, 120);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [roleFilter, setRoleFilter] = useState('');
  const [sort, setSort] = useState<SortKey>('name');
  const [layout, setLayout] = useState<Layout>('cards');
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const [editor, setEditor] = useState<EditorState>(null);
  const [bulkRole, setBulkRole] = useState('');
  const [bulkDays, setBulkDays] = useState('30');
  const [now, setNow] = useState(Date.now());
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(t);
  }, []);

  const counts = useMemo(() => {
    const c = { all: members.length, active: 0, suspended: 0, expired: 0, online: 0 };
    for (const m of members) {
      c[statusOf(m)]++;
      if (m.online) c.online++;
    }
    return c;
  }, [members]);

  const shown = useMemo(() => {
    const q = dSearch.trim().toLowerCase();
    const list = members.filter((m) => {
      const st = statusOf(m);
      if (statusFilter === 'online' ? !m.online : statusFilter !== 'all' && st !== statusFilter) return false;
      if (roleFilter === '__none') {
        if (m.roleId) return false;
      } else if (roleFilter && m.roleId !== roleFilter) return false;
      if (!q) return true;
      return [m.name, m.discordId, m.characters.join(' '), m.note].join(' ').toLowerCase().indexOf(q) !== -1;
    });
    const roleName = (m: StaffMemberView): string => {
      const r = roleOf(staff, m.roleId);
      return r ? r.name : '~';
    };
    list.sort((a, b) => {
      if (sort === 'seen') return (b.online ? Infinity : b.lastSeenAt) - (a.online ? Infinity : a.lastSeenAt) || (a.name || '').localeCompare(b.name || '', 'fr');
      if (sort === 'role') return roleName(a).localeCompare(roleName(b), 'fr') || (a.name || '').localeCompare(b.name || '', 'fr');
      return (a.name || a.discordId).localeCompare(b.name || b.discordId, 'fr', { sensitivity: 'base' });
    });
    return list;
  }, [members, dSearch, statusFilter, roleFilter, sort, staff]);

  const selectedIds = useMemo(() => members.filter((m) => selection[m.discordId]).map((m) => m.discordId), [members, selection]);
  const allShownSelected = shown.length > 0 && shown.every((m) => selection[m.discordId]);

  const toggleOne = useCallback((id: string) => setSelection((s) => ({ ...s, [id]: !s[id] })), []);
  const toggleAll = () => {
    const next: Record<string, boolean> = { ...selection };
    for (const m of shown) next[m.discordId] = !allShownSelected;
    setSelection(next);
  };

  const closeEditor = useCallback(() => {
    setEditor(null);
    window.setTimeout(() => {
      const el = listRef.current ? (listRef.current.querySelector('button, input') as HTMLElement | null) : null;
      if (el) el.focus();
    }, 0);
  }, []);

  // ---- bulk actions -------------------------------------------------------

  const names = (ids: string[]): string => {
    const list = ids.map((id) => {
      const m = members.find((x) => x.discordId === id);
      return m && m.name ? m.name : id;
    });
    return list.length > 4 ? `${list.slice(0, 4).join(', ')} et ${list.length - 4} autres` : list.join(', ');
  };

  const bulk = (action: 'suspend' | 'activate' | 'setRole' | 'remove' | 'extend', title: string, body: string, confirmLabel: string, danger: boolean, extra?: Record<string, unknown>) => {
    const ids = selectedIds.slice();
    confirm({
      title,
      body: (
        <>
          <span>{body}</span>
          <span className="admin-panel__quote">{names(ids)}</span>
        </>
      ),
      confirmLabel,
      danger,
      onConfirm: () => {
        for (const id of ids) {
          if (action === 'suspend') patchLocal(id, { active: false });
          if (action === 'activate') patchLocal(id, { active: true, expired: false });
          if (action === 'setRole') patchLocal(id, { roleId: (extra && (extra.roleId as string)) || null, caps: [], denied: [] });
          if (action === 'extend') {
            const m = members.find((x) => x.discordId === id);
            if (m) patchLocal(id, { expiresAt: extendedExpiry(m, Number(extra && extra.days) || 0, Date.now()), expired: false });
          }
        }
        act('adminStaffBulk', { discordIds: ids, action, ...(extra || {}) }, 'Action groupée');
        setSelection({});
      },
    });
  };

  const n = selectedIds.length;
  const days = Math.max(1, Math.min(365, Math.floor(Number(bulkDays)) || 30));

  // ---- the add flow -------------------------------------------------------

  const openMember = (id: string) => setEditor({ kind: 'member', discordId: id, seed: null });

  const editorMember = editor && editor.kind === 'member' ? members.find((m) => m.discordId === editor.discordId) || null : null;

  const roleFor = (m: StaffMemberView): StaffRole | null => roleOf(staff, m.roleId);
  const order = useMemo(() => grantable(staff).map((c) => c.id), [staff]);
  const capCount = (m: StaffMemberView): number => finalCaps(order, roleFor(m), m.caps, m.denied).length;

  const editing = editor !== null;

  return (
    <div className={'admin-panel__staffgrid' + (editing ? ' admin-panel__staffgrid--editor' : '')}>
      <div className="admin-panel__staffmain" ref={listRef}>
        <div className="admin-panel__toolbar">
          <div className="admin-panel__searchfield admin-panel__input--grow">
            <Icon name="search" size={15} className="admin-panel__searchfield-icon" />
            <input className="admin-panel__input admin-panel__input--block" type="search" placeholder="Nom, Discord ID, personnage, note" aria-label="Filtrer les membres du staff" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <label className="admin-panel__select-wrap">
            <span className="admin-panel__select-label">Rôle</span>
            <select className="admin-panel__select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="">Tous</option>
              <option value="__none">Sans rôle</option>
              {staff.roles.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </label>
          <label className="admin-panel__select-wrap">
            <span className="admin-panel__select-label">Tri</span>
            <select className="admin-panel__select" value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
              <option value="name">Nom</option>
              <option value="seen">Dernière connexion</option>
              <option value="role">Rôle</option>
            </select>
          </label>
          <div className="admin-panel__iconbtns" role="group" aria-label="Affichage">
            <IconButton icon="list" label="Afficher en liste" active={layout === 'cards'} onClick={() => setLayout('cards')} />
            <IconButton icon="table" label="Afficher en tableau" active={layout === 'table'} onClick={() => setLayout('table')} />
          </div>
          <button type="button" className="admin-panel__button admin-panel__button--primary" onClick={() => setEditor({ kind: 'add' })}>
            <Icon name="plus" size={14} /> Ajouter
          </button>
        </div>

        <div className="admin-panel__toolbar admin-panel__toolbar--tight">
          <Segmented<StatusFilter>
            label="Filtrer par statut"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { id: 'all', label: 'Tous', count: counts.all },
              { id: 'active', label: 'Actifs', count: counts.active },
              { id: 'suspended', label: 'Suspendus', count: counts.suspended },
              { id: 'expired', label: 'Expirés', count: counts.expired },
              { id: 'online', label: 'En ligne', count: counts.online },
            ]}
          />
          <label className="admin-panel__check">
            <input type="checkbox" checked={allShownSelected} onChange={toggleAll} disabled={shown.length === 0} />
            Tout sélectionner ({shown.length})
          </label>
        </div>

        {n > 0 ? (
          <div className="admin-panel__bulkbar" role="region" aria-label="Actions groupées">
            <span className="admin-panel__bulkcount">{n} {plural(n, 'membre sélectionné', 'membres sélectionnés')}</span>
            <button type="button" className="admin-panel__button admin-panel__button--sm" onClick={() => bulk('suspend', `Suspendre ${n} ${plural(n, 'membre', 'membres')} ?`, 'Ils perdent tout accès au panel jusqu\'à réactivation.', 'Suspendre', false)}>Suspendre</button>
            <button type="button" className="admin-panel__button admin-panel__button--sm" onClick={() => bulk('activate', `Réactiver ${n} ${plural(n, 'membre', 'membres')} ?`, 'Ils retrouvent leurs permissions.', 'Réactiver', false)}>Activer</button>
            <span className="admin-panel__bulkgroup">
              <select className="admin-panel__select admin-panel__select--sm" aria-label="Rôle à appliquer" value={bulkRole} onChange={(e) => setBulkRole(e.target.value)}>
                <option value="">Choisir un rôle</option>
                {staff.roles.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
              <button
                type="button"
                className="admin-panel__button admin-panel__button--sm"
                disabled={!bulkRole}
                onClick={() => bulk('setRole', `Donner le rôle ${(roleOf(staff, bulkRole) || { name: '' }).name} à ${n} ${plural(n, 'membre', 'membres')} ?`, 'Leurs permissions ajoutées ou retirées à la main sont remplacées par celles du rôle.', 'Appliquer le rôle', false, { roleId: bulkRole })}
              >
                Changer de rôle
              </button>
            </span>
            <span className="admin-panel__bulkgroup">
              <input className="admin-panel__input admin-panel__input--xs" type="number" min={1} max={365} aria-label="Nombre de jours à ajouter" value={bulkDays} onChange={(e) => setBulkDays(e.target.value)} />
              <button type="button" className="admin-panel__button admin-panel__button--sm" onClick={() => bulk('extend', `Prolonger de ${days} jours ?`, 'La date d\'échéance recule de ce nombre de jours (à partir d\'aujourd\'hui si elle est dépassée).', 'Prolonger', false, { days })}>
                Prolonger (jours)
              </button>
            </span>
            <button type="button" className="admin-panel__button admin-panel__button--danger admin-panel__button--sm" onClick={() => bulk('remove', `Retirer ${n} ${plural(n, 'membre', 'membres')} du staff ?`, 'Ils perdent toutes leurs permissions immédiatement.', 'Retirer du staff', true)}>Retirer</button>
            <button type="button" className="admin-panel__button admin-panel__button--secondary admin-panel__button--sm" onClick={() => setSelection({})}>Désélectionner</button>
          </div>
        ) : null}

        {shown.length === 0 ? (
          <Empty
            title={members.length ? 'Aucun membre ne correspond' : 'Aucun membre du staff en jeu'}
            text={members.length ? 'Changez le filtre, le rôle ou la recherche.' : 'Ajoutez une personne et donnez-lui un rôle : elle accède aussitôt aux menus correspondants.'}
          >
            {!members.length ? (
              <button type="button" className="admin-panel__button admin-panel__button--primary admin-panel__empty-action" onClick={() => setEditor({ kind: 'add' })}>Ajouter le premier membre</button>
            ) : null}
          </Empty>
        ) : layout === 'cards' ? (
          <ul className="admin-panel__cards admin-panel__cards--members">
            {shown.map((m) => (
              <MemberCard key={m.discordId} m={m} role={roleFor(m)} caps={capCount(m)} selected={!!selection[m.discordId]} active={!!editor && editor.kind === 'member' && editor.discordId === m.discordId} now={now} onToggle={toggleOne} onOpen={openMember} />
            ))}
          </ul>
        ) : (
          <div className="admin-panel__tablewrap">
            <table className="admin-panel__dtable">
              <thead>
                <tr>
                  <th className="admin-panel__col-check"><span className="admin-panel__sr">Sélection</span></th>
                  <th>Membre</th>
                  <th className="admin-panel__col-hide">Discord ID</th>
                  <th>Rôle</th>
                  <th className="admin-panel__num">Permissions</th>
                  <th>Dernière connexion</th>
                  <th className="admin-panel__col-hide">Échéance</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((m) => (
                  <MemberTableRow key={m.discordId} m={m} role={roleFor(m)} caps={capCount(m)} selected={!!selection[m.discordId]} active={!!editor && editor.kind === 'member' && editor.discordId === m.discordId} now={now} onToggle={toggleOne} onOpen={openMember} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <section className="admin-panel__section" aria-label="Propriétaires">
          <h3 className="admin-panel__group-title">Propriétaires</h3>
          <p className="admin-panel__hint">
            Les propriétaires sont les identifiants Discord du fichier de configuration du serveur. Ils ont toutes les permissions et sont les seuls à gérer le staff. Cette liste ne se modifie pas depuis le panel.
          </p>
          {staff.owners.length === 0 ? (
            <p className="admin-panel__hint">Aucun propriétaire déclaré.</p>
          ) : (
            <ul className="admin-panel__owners">
              {staff.owners.map((o) => (
                <li key={o.discordId} className="admin-panel__owner">
                  <span className={'admin-panel__dot' + (o.online ? ' admin-panel__dot--online' : '')} title={o.online ? 'En ligne' : 'Hors ligne'} />
                  <span className="admin-panel__cell admin-panel__cell--name">{o.characters.length ? o.characters.join(', ') : 'Personnage non reconnu'}</span>
                  <span className="admin-panel__cell admin-panel__cell--muted">{o.discordId}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {editor && editor.kind === 'add' ? (
        <AddMember
          members={members}
          onCancel={closeEditor}
          onPick={(seed) => {
            const existing = members.find((m) => m.discordId === seed.discordId);
            setEditor({ kind: 'member', discordId: seed.discordId, seed: existing ? null : seed });
          }}
        />
      ) : null}

      {editor && editor.kind === 'member' ? (
        <MemberEditor
          key={editor.discordId}
          staff={staff}
          member={editorMember}
          seed={editor.seed}
          audit={audit}
          onClose={closeEditor}
          onSaved={(d: Draft) => {
            if (!editorMember) return;
            patchLocal(d.discordId, { name: d.name, roleId: d.roleId, caps: d.caps, denied: d.denied, note: d.note, active: d.active, expiresAt: d.expiresAt, expired: !!d.expiresAt && d.expiresAt < Date.now() });
          }}
        />
      ) : null}
    </div>
  );
};

// ---------------------------------------------------------------------------

interface RowProps {
  m: StaffMemberView;
  role: StaffRole | null;
  caps: number;
  selected: boolean;
  active: boolean;
  now: number;
  onToggle: (id: string) => void;
  onOpen: (id: string) => void;
}

const MemberCard = React.memo(function MemberCard({ m, role, caps, selected, active, now, onToggle, onOpen }: RowProps) {
  const st: MemberStatus = statusOf(m);
  const expiry = describeExpiry(m, now);
  return (
    <li className={'admin-panel__mcard' + (active ? ' admin-panel__mcard--active' : '') + (st !== 'active' ? ' admin-panel__mcard--dim' : '')}>
      <input type="checkbox" className="admin-panel__mcard-check" checked={selected} onChange={() => onToggle(m.discordId)} aria-label={`Sélectionner ${m.name || m.discordId}`} />
      <button type="button" className="admin-panel__mcard-body" onClick={() => onOpen(m.discordId)} aria-pressed={active}>
        <span className="admin-panel__mcard-top">
          <span className={'admin-panel__dot' + (m.online ? ' admin-panel__dot--online' : '')} title={m.online ? 'En ligne' : 'Hors ligne'} />
          <span className="admin-panel__mcard-name">{m.name || 'Sans nom'}</span>
          <StatusPill status={st} />
        </span>
        <span className="admin-panel__mcard-line">
          <RoleChip role={role} />
          <span className="admin-panel__cell--muted">{caps} {plural(caps, 'menu', 'menus')}</span>
          {m.caps.length > 0 ? <span className="admin-panel__tag">+{m.caps.length} ajouté{m.caps.length > 1 ? 's' : ''}</span> : null}
          {m.denied.length > 0 ? <span className="admin-panel__tag admin-panel__tag--danger">-{m.denied.length} retiré{m.denied.length > 1 ? 's' : ''}</span> : null}
        </span>
        <span className="admin-panel__mcard-meta">
          {m.discordId} - {lastSeen(m, now)}
          {expiry ? ` - ${expiry}` : ''}
        </span>
        {m.note ? <span className="admin-panel__mcard-meta">{m.note}</span> : null}
      </button>
    </li>
  );
});

const MemberTableRow = React.memo(function MemberTableRow({ m, role, caps, selected, active, now, onToggle, onOpen }: RowProps) {
  const st = statusOf(m);
  return (
    <tr className={(active ? 'is-active ' : '') + (st !== 'active' ? 'is-dim' : '')} onClick={() => onOpen(m.discordId)}>
      <td className="admin-panel__col-check" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={selected} onChange={() => onToggle(m.discordId)} aria-label={`Sélectionner ${m.name || m.discordId}`} />
      </td>
      <td>
        <button type="button" className="admin-panel__rowlink" onClick={(e) => { e.stopPropagation(); onOpen(m.discordId); }}>
          <span className={'admin-panel__dot' + (m.online ? ' admin-panel__dot--online' : '')} />
          {m.name || 'Sans nom'}
        </button>
      </td>
      <td className="admin-panel__col-hide admin-panel__cell--muted">{m.discordId}</td>
      <td><RoleChip role={role} empty="Aucun" /></td>
      <td className="admin-panel__num">{caps}</td>
      <td className="admin-panel__cell--muted">{lastSeen(m, now)}</td>
      <td className="admin-panel__col-hide admin-panel__cell--muted">{describeExpiry(m, now) || 'Permanent'}</td>
      <td><StatusPill status={st} /></td>
    </tr>
  );
});

// ---------------------------------------------------------------------------

const AddMember: React.FC<{ members: StaffMemberView[]; onCancel: () => void; onPick: (seed: { name: string; discordId: string }) => void }> = ({ members, onCancel, onPick }) => {
  const { send, onEscape } = useAdmin();
  const [query, setQuery] = useState('');
  const dQuery = useDebounced(query, 250);
  const [results, setResults] = useState<StaffCandidate[] | null>(null);
  const [directId, setDirectId] = useState('');
  const wanted = useRef('');

  useEffect(() => onEscape(onCancel), [onEscape, onCancel]);

  useEffect(() => {
    const onCandidates = (e: Event) => {
      const d = (e as CustomEvent).detail as { query: string; results: StaffCandidate[] };
      if (d && d.query === wanted.current.trim().toLowerCase().slice(0, 48)) setResults(Array.isArray(d.results) ? d.results : []);
    };
    window.addEventListener('skymp5-client:adminStaffCandidates', onCandidates);
    return () => window.removeEventListener('skymp5-client:adminStaffCandidates', onCandidates);
  }, []);

  useEffect(() => {
    wanted.current = dQuery;
    setResults(null);
    send({ action: 'adminStaffCandidates', query: dQuery });
  }, [dQuery, send]);

  const idOk = DISCORD_ID_RE.test(directId.trim());

  return (
    <aside className="admin-panel__editor" aria-label="Ajouter un membre">
      <div className="admin-panel__editor-head">
        <h3 className="admin-panel__editor-title">Ajouter un membre</h3>
        <button type="button" className="admin-panel__button admin-panel__button--secondary" onClick={onCancel}>Annuler</button>
      </div>
      <div className="admin-panel__editor-scroll">
        <label className="admin-panel__field-label admin-panel__field-label--first" htmlFor="ap-cand">Chercher un personnage par son nom</label>
        <input id="ap-cand" className="admin-panel__input admin-panel__input--block" type="search" placeholder="Nom du personnage" value={query} autoFocus onChange={(e) => setQuery(e.target.value)} />
        <div className="admin-panel__candidates" role="list">
          {results === null ? (
            <Loading text="Recherche en cours" shape="rows" />
          ) : results.length === 0 ? (
            <p className="admin-panel__hint">Aucun personnage trouvé. Vous pouvez saisir directement un identifiant Discord ci-dessous.</p>
          ) : (
            results.map((c) => {
              const already = !!c.discordId && members.some((m) => m.discordId === c.discordId);
              return (
                <button key={c.profileId + ':' + c.name} type="button" role="listitem" className="admin-panel__candidate" onClick={() => onPick({ name: c.name, discordId: c.discordId || '' })}>
                  <span className={'admin-panel__dot' + (c.online ? ' admin-panel__dot--online' : '')} />
                  <span className="admin-panel__cell admin-panel__cell--name">{c.name}</span>
                  {already ? (
                    <span className="admin-panel__tag">Déjà du staff</span>
                  ) : c.discordId ? (
                    <span className="admin-panel__cell admin-panel__cell--muted">{c.discordId}</span>
                  ) : (
                    <span className="admin-panel__tag admin-panel__tag--warn">Identifiant inconnu</span>
                  )}
                </button>
              );
            })
          )}
        </div>

        <div className="admin-panel__sep" />
        <label className="admin-panel__field-label admin-panel__field-label--first" htmlFor="ap-direct">Ou saisir un identifiant Discord</label>
        <div className="admin-panel__gold-row">
          <input
            id="ap-direct"
            className="admin-panel__input"
            inputMode="numeric"
            placeholder="Identifiant de 17 à 20 chiffres"
            value={directId}
            onChange={(e) => setDirectId(e.target.value.replace(/\s/g, ''))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && idOk) onPick({ name: '', discordId: directId.trim() });
            }}
          />
          <button type="button" className="admin-panel__button" disabled={!idOk} onClick={() => onPick({ name: '', discordId: directId.trim() })}>Continuer</button>
        </div>
        <p className="admin-panel__hint">
          Un personnage marqué « Identifiant inconnu » ne s'est pas encore reconnecté depuis que le serveur enregistre les identifiants Discord. Saisissez alors son identifiant à la main.
        </p>
      </div>
    </aside>
  );
};
